import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer, useImmerReducer } from "use-immer";

import { IgLink, IgTypography } from "@intelligems/ig-theme";
import { ExperienceTestType, OnboardingStateType } from "@intelligems/ig-types";
import { Dialog, Grid } from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { initialOnboardingState } from "../../../api/initials";
import {
  OnboardingReducer,
  OnboardingReducerAction,
} from "../../../api/reducers/onboardingReducer";
import { useIsAdmin } from "../../../api/user-auths/is-admin";
import { useUser } from "../../../api/user-auths/login";
import { getDialog, setDialog } from "../../../store/slices/global";
import { OnboardingSteps } from "../../../types/organization";
import { pricingPage } from "../../../utils/linkSheet";
import openNewTab from "../../../utils/openNewTab";
import { checkSubscriptionStatus } from "../../ScopeWrappers/helpers";

import { NavigateButtons } from "./Utils/NavigateButtons";
import { OnboardingStepper } from "./OnboardingStepper";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: "14px !important",
  borderRadius: "0px",
  overflow: "hidden",
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#E1E1E1",
  },
}));

export const OnboardingFlow = () => {
  const dispatch = useDispatch();
  const user = useUser();
  const dialog = useSelector(getDialog("onboarding"));
  const isAdmin = useIsAdmin();
  const isSubscriptionActive = checkSubscriptionStatus(true);

  const [resetOnboardingFlow, setResetOnboardingFlow] =
    useImmer<boolean>(false);

  const [onboardingState, dispatchOnboardingState] = useImmerReducer<
    OnboardingStateType,
    OnboardingReducerAction
  >(OnboardingReducer, initialOnboardingState);
  const handleCloseDialog = () => {
    dispatch(setDialog({ dialog: "onboarding", open: false }));
  };
  const selectedTestTypeConfig = useConfiguration<string>(
    "onboarding_test_flow"
  );
  const [selectedTestType, setSelectedTestType] = useImmer<
    ExperienceTestType | ""
  >(selectedTestTypeConfig?.value || "");

  const handleBack = () => {
    if (
      onboardingState.activeStep === 4 &&
      selectedTestTypeConfig!.value !== ""
    ) {
      dispatchOnboardingState({
        type: "gotoStep",
        value: 2,
      });
    } else {
      dispatchOnboardingState({
        type: "buttonClick",
        payload: {
          direction: "back",
        },
      });
    }
  };

  useEffect(() => {
    if (user.isSuccess && isAdmin !== undefined) {
      //} && isAdmin !== undefined) {
      if (user.data.user.showWelcome) {
        dispatch(setDialog({ dialog: "onboarding", open: true }));
      } else if (!isSubscriptionActive && !isAdmin) {
        //&& !isAdmin) {
        dispatch(setDialog({ dialog: "onboarding", open: true }));
        dispatchOnboardingState({ type: "gotoStep", value: 4 });
      }
    }
  }, [user.isSuccess, user.data, isSubscriptionActive, isAdmin]);

  // reset onboarding flow on complete of flow
  useEffect(() => {
    if (resetOnboardingFlow) {
      dispatchOnboardingState({
        type: "gotoStep",
        value: 0,
      });
      setResetOnboardingFlow(false);
    }
  }, [resetOnboardingFlow]);

  if (user.isSuccess) {
    const steps: OnboardingSteps[] = [
      {
        label: `Howdy, ${user.data.user.firstName}!`,
        description: (
          <IgTypography sx={{ fontSize: "16px" }}>
            Let us walk you through Intelligems and get your first test set up
          </IgTypography>
        ),
      },
      {
        label: "Let's Get Started",
        description:
          "Which Intelligems product would you like to get started with?\n You can always come back here to explore via the Help menu.",
      },
      {
        label: `Get started with ${
          selectedTestType === "campaign"
            ? "Campaigns"
            : selectedTestType === "content"
              ? "Content Testing"
              : selectedTestType === "pricing"
                ? "Price Testing"
                : "Shipping Testing"
        }`,
        description: (
          <Grid item container direction={"column"}>
            <Grid item>
              <IgTypography sx={{ fontSize: "18px" }} igStyle={"semibold"}>
                {selectedTestType === "campaign"
                  ? "A smarter way to offer discounts"
                  : selectedTestType === "content"
                    ? "Optimize your store for conversion"
                    : selectedTestType === "pricing"
                      ? "Find the “sweet spot” for your prices"
                      : "Optimized shipping rates, delivered"}
              </IgTypography>
            </Grid>
            <Grid item>
              <IgTypography sx={{ fontStyle: "italic" }}>
                {selectedTestType === "campaign"
                  ? "Stop leaving money on the table"
                  : selectedTestType === "content"
                    ? "A/B test anything on your Shopify store"
                    : selectedTestType === "pricing"
                      ? "Measure Price Elasticity and Find Extra Margin"
                      : "Turn shipping from cost to profit"}
              </IgTypography>
            </Grid>
          </Grid>
        ),
      },
      {
        label: "How did you hear about us?",
        description:
          "Tell us how you found us—whether through a recommendation, online search,\n or another avenue. Your unique journey matters and helps us enhance your\n experience with our offerings.",
      },
      {
        label: "CUSTOMIZE YOUR PLAN",
        description: (
          <IgTypography sx={{ fontSize: "18px", fontWeight: 600 }}>
            7-day free trial for new customers.
            <IgTypography sx={{ fontSize: "16px" }}>
              {
                "\nSelect any of the plans below. Detailed plan comparisons available"
              }{" "}
            </IgTypography>
            <IgLink
              sx={{ fontSize: "16px" }}
              onClick={() => {
                openNewTab(pricingPage, "Website Pricing Page");
              }}
            >
              here.
            </IgLink>
          </IgTypography>
        ),
      },
    ];

    return (
      <Dialog
        sx={{
          marginLeft: "190px",
          marginRight: "190px",
        }}
        PaperProps={{
          sx: {
            maxHeight: "90%",
            zIndex: 1,
            position: "relative",
          },
        }}
        disableEscapeKeyDown={true}
        fullWidth={true}
        maxWidth={"lg"}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCloseDialog();
          }
        }}
        open={dialog.isOpen}
      >
        <div>
          {onboardingState.activeStep > 0 && (
            <div>
              <BorderLinearProgress
                value={onboardingState.progress}
                variant={"determinate"}
              />
              <NavigateButtons
                onboardingState={onboardingState}
                handleBack={handleBack}
                steps={steps}
              />
            </div>
          )}
          <Grid
            container
            direction={"column"}
            sx={{
              padding: "40px",
              paddingTop: onboardingState.activeStep !== 0 ? 0 : "40px",
            }}
          >
            <OnboardingStepper
              onboardingState={onboardingState}
              dispatchOnboardingState={dispatchOnboardingState}
              steps={steps}
              selectedTestType={selectedTestType}
              setSelectedTestType={setSelectedTestType}
              setResetOnboardingFlow={setResetOnboardingFlow}
            />
          </Grid>
        </div>
      </Dialog>
    );
  }
  return null;
};
